import { useState } from "react";
import { Bell, Check, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import useNotifications from "../../customHooks/useNotifications";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  1) return "Just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  <  7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

function NotificationSkeleton() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
      <Skeleton circle width={40} height={40} />
      <div style={{ flex: 1 }}>
        <Skeleton height={14} width="50%" style={{ marginBottom: 6 }} />
        <Skeleton height={12} count={2} />
      </div>
    </div>
  );
}

function NotificationItem({ item, onMarkRead, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = item.message?.length > 100;
  const displayMsg = expanded || !isLong ? item.message : item.message?.slice(0, 100) + "…";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      style={{
        display: "flex",
        gap: 12,
        padding: "14px 16px",
        borderBottom: "1px solid #f8fafc",
        background: item.isRead ? "#fff" : "#f0f7ff",
        borderLeft: item.isRead ? "3px solid transparent" : "3px solid #0d6efd",
        transition: "background 0.2s",
      }}
    >
      {/* Icon */}
      <div style={{
        width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
        background: item.isRead ? "#f1f5f9" : "#dbeafe",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Bell size={17} color={item.isRead ? "#9ca3af" : "#0d6efd"} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 13, fontWeight: item.isRead ? 500 : 700,
                color: "#111827", fontFamily: "'Inter', sans-serif",
              }}>
                {item.title}
              </span>
              {!item.isRead && (
                <span style={{
                  background: "#0d6efd", color: "#fff",
                  borderRadius: 20, padding: "1px 7px",
                  fontSize: 10, fontWeight: 700, fontFamily: "'Inter', sans-serif",
                  flexShrink: 0,
                }}>
                  New
                </span>
              )}
            </div>
            <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
              {timeAgo(item.createdAt)}
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {!item.isRead && (
              <button
                onClick={() => onMarkRead(item._id)}
                title="Mark as read"
                style={{
                  width: 30, height: 30, borderRadius: "50%", border: "1px solid #bbf7d0",
                  background: "#f0fdf4", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Check size={14} color="#059669" strokeWidth={2.5} />
              </button>
            )}
            <button
              onClick={() => onDelete(item._id)}
              title="Delete"
              style={{
                width: 30, height: 30, borderRadius: "50%", border: "1px solid #fecaca",
                background: "#fff5f5", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Trash2 size={14} color="#ef4444" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Message */}
        {item.message && (
          <p style={{
            fontSize: 13, color: "#6b7280", margin: "5px 0 0",
            lineHeight: 1.55, fontFamily: "'Inter', sans-serif",
          }}>
            {displayMsg}
          </p>
        )}

        {/* Expand toggle */}
        {isLong && (
          <button
            onClick={() => setExpanded(p => !p)}
            style={{
              background: "none", border: "none", padding: "4px 0 0",
              fontSize: 12, fontWeight: 600, color: "#0d6efd",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 3,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {expanded ? <><ChevronUp size={13} /> Show less</> : <><ChevronDown size={13} /> Show more</>}
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function Notifications() {
  const { notifications, unreadCount, loading, hasMore, markAsRead, markAllAsRead, deleteNotification, fetchMore } = useNotifications();

  // The API's own count covers the whole inbox, not just the loaded page.
  const unread = unreadCount;

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <h5 style={{ fontWeight: 800, fontSize: 22, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
          Notifications
        </h5>
        {unread > 0 && (
          <span style={{
            background: "#0d6efd", color: "#fff",
            borderRadius: 20, padding: "2px 10px",
            fontSize: 12, fontWeight: 700, fontFamily: "'Inter', sans-serif",
          }}>
            {unread} new
          </span>
        )}
        {unread > 0 && (
          <button
            onClick={markAllAsRead}
            style={{
              marginLeft: "auto", background: "none", border: "none", padding: 0,
              fontSize: 12, fontWeight: 600, color: "#0d6efd", cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Card container */}
      <div style={{
        background: "#fff",
        border: "1.5px solid #f1f5f9",
        borderRadius: 14,
        overflow: "hidden",
      }}>

        {/* Loading skeletons */}
        {loading && notifications.length === 0 && (
          [1,2,3,4,5].map(i => <NotificationSkeleton key={i} />)
        )}

        {/* Empty state */}
        {!loading && notifications.length === 0 && (
          <div style={{ padding: "60px 20px", textAlign: "center", fontFamily: "'Inter', sans-serif" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "#f1f5f9", margin: "0 auto 16px",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Bell size={28} color="#d1d5db" />
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>
              No notifications yet
            </p>
            <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
              We'll let you know when something arrives
            </p>
          </div>
        )}

        {/* List */}
        <AnimatePresence initial={false}>
          {notifications.map(item => (
            <NotificationItem
              key={item._id}
              item={item}
              onMarkRead={markAsRead}
              onDelete={deleteNotification}
            />
          ))}
        </AnimatePresence>

        {/* Load more */}
        {hasMore && (
          <div style={{ padding: "14px", textAlign: "center", borderTop: "1px solid #f8fafc" }}>
            <button
              onClick={fetchMore}
              disabled={loading}
              style={{
                padding: "9px 24px", borderRadius: 8,
                border: "1.5px solid #e5e7eb",
                background: loading ? "#f9fafb" : "#fff",
                fontSize: 13, fontWeight: 600, color: loading ? "#9ca3af" : "#374151",
                fontFamily: "'Inter', sans-serif", cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}

        {/* Bottom loading indicator (pagination) */}
        {loading && notifications.length > 0 && (
          <div style={{ padding: "14px 16px", borderTop: "1px solid #f8fafc" }}>
            <NotificationSkeleton />
          </div>
        )}
      </div>
    </div>
  );
}
